import { Avatar, Button } from '@/shared/ui/primitives';
import type { FC, ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import type { Client } from '@/types';
import { TEST_IDS } from '@/testIds';
import { AppModal, CreditBadge, ModalContent, ModalFooter, ModalTopBar } from '@/shared/ui';
import { PassIcon } from '@/shared/ui/icons/PassIcon';
import { getInitials } from '@/shared/lib/client';
import { getPassDescription } from '../model/passDescription';
import styles from './ClientModal.module.css';

interface ClientModalProps {
  isOpen: boolean;
  client: Client | null;
  onClose: () => void;
  onMarkVisit: () => void;
  onEdit: () => void;
  onAddPass?: () => void;
  onEditPass?: () => void;
  children?: ReactNode;
  canMarkVisit?: boolean;
  isLoading?: boolean;
  recordDisabled?: boolean;
}

export const ClientModal: FC<ClientModalProps> = ({
  isOpen,
  client,
  onClose,
  onMarkVisit,
  onEdit,
  onAddPass,
  onEditPass,
  children,
  canMarkVisit = true,
  isLoading = false,
  recordDisabled = false,
}) => {
  const Summary = client?.pass && onEditPass ? 'button' : 'div';
  const showAddPassButton = !canMarkVisit && onAddPass;

  return (
    <AppModal
      open={isOpen}
      dismissible={!isLoading}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
    >
      {client && (
        <ModalContent>
          <ModalTopBar
            title={
              <span className={styles.identity}>
                <Avatar initials={getInitials(client.name)} size={48} />
                <span className={styles.clientName}>{client.name}</span>
              </span>
            }
            onCancel={onClose}
            cancelDisabled={isLoading}
          />
          <Summary
            className={`${styles.summary} ${Summary === 'button' ? styles.summaryButton : ''}`}
            {...(Summary === 'button'
              ? {
                  type: 'button' as const,
                  onClick: onEditPass,
                  disabled: isLoading,
                  'aria-label': `Edit pass ${client.pass?.name}`,
                }
              : {})}
          >
            <PassIcon className={styles.passIcon} width={28} height={28} aria-hidden="true" />
            <span className={styles.summaryBody}>
              <span className={styles.passName}>{client.pass?.name ?? 'No active pass'}</span>
              <span className={styles.passDetails}>{getPassDescription(client).text}</span>
            </span>
            {client.pass && <CreditBadge pass={client.pass} />}
            {Summary === 'button' && (
              <ChevronRight className={styles.passIcon} size={18} aria-hidden="true" />
            )}
          </Summary>
          {recordDisabled && canMarkVisit && (
            <p className={styles.refreshMessage} role="status">
              Refresh the client list before recording another visit.
            </p>
          )}
          <ModalFooter>
            {canMarkVisit && (
              <Button
                variant="primary"
                onClick={onMarkVisit}
                loading={isLoading}
                disabled={recordDisabled}
                fullWidth
                data-testid={TEST_IDS.markVisitButton}
              >
                Record visit
              </Button>
            )}

            {showAddPassButton && (
              <Button
                variant="primary"
                onClick={onAddPass}
                disabled={isLoading}
                fullWidth
                data-testid={TEST_IDS.addPassButton}
              >
                Add pass
              </Button>
            )}

            <Button
              variant="secondary"
              onClick={onEdit}
              disabled={isLoading}
              fullWidth
              data-testid={TEST_IDS.profileClientButton}
            >
              View profile
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
      {children}
    </AppModal>
  );
};
